import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks';
import {
  HealthModuleType,
  WellnessConsent,
  HormonalHealthInput,
  IntimateHealthInput,
  SkinHairHealthInput,
  MentalHealthInput,
  RiskLevel,
} from '../types';
import './WellnessPage.css';

// Hormone education cards data
const hormoneCards = [
  {
    hormone: 'testosterone',
    title: 'Testosterone',
    icon: '💪',
    description: 'Primary male sex hormone, also important for women. Affects muscle mass, mood, and energy.',
    symptoms: ['Low energy', 'Muscle loss', 'Mood changes', 'Low libido'],
    lifestyleFactors: ['Sleep quality', 'Exercise', 'Stress', 'Diet'],
  },
  {
    hormone: 'estrogen',
    title: 'Estrogen',
    icon: '🌸',
    description: 'Primary female sex hormone. Important for reproductive health, bone density, and mood.',
    symptoms: ['Irregular periods', 'Hot flashes', 'Mood swings', 'Weight changes'],
    lifestyleFactors: ['Body weight', 'Exercise', 'Stress', 'Diet'],
  },
  {
    hormone: 'thyroid',
    title: 'Thyroid Hormones',
    icon: '⚡',
    description: 'Controls metabolism, energy production, and body temperature.',
    symptoms: ['Fatigue', 'Weight changes', 'Hair loss', 'Temperature sensitivity'],
    lifestyleFactors: ['Iodine intake', 'Stress', 'Sleep', 'Exercise'],
  },
  {
    hormone: 'cortisol',
    title: 'Cortisol',
    icon: '😰',
    description: 'Stress hormone. Helps regulate metabolism, immune response, and blood pressure.',
    symptoms: ['Chronic stress', 'Sleep issues', 'Weight gain', 'Anxiety'],
    lifestyleFactors: ['Stress management', 'Sleep', 'Caffeine', 'Exercise timing'],
  },
  {
    hormone: 'insulin',
    title: 'Insulin',
    icon: '🍬',
    description: 'Regulates blood sugar levels. Important for energy and fat storage.',
    symptoms: ['Energy crashes', 'Sugar cravings', 'Weight gain', 'Brain fog'],
    lifestyleFactors: ['Diet quality', 'Meal timing', 'Exercise', 'Sleep'],
  },
];

// Intimate health concerns
const intimateConcerns = {
  male: [
    'Low libido / decreased desire',
    'Erectile concerns',
    'Premature concerns',
    'Performance anxiety',
    'Testosterone-related concerns',
    'Fertility questions',
    'General sexual wellness',
  ],
  female: [
    'Low libido / decreased desire',
    'Menstrual irregularities',
    'PCOS/PCOD related concerns',
    'Menopausal symptoms',
    'Fertility questions',
    'Vaginal health concerns',
    'General sexual wellness',
  ],
  common: [
    'Relationship intimacy',
    'Stress affecting intimacy',
    'Body image concerns',
    'Sexual education',
  ],
};

// Skin/Hair concerns
const skinHairConcerns = [
  { id: 'acne', label: 'Acne / Breakouts', icon: '😟' },
  { id: 'hair_fall', label: 'Hair Fall / Thinning', icon: '💇' },
  { id: 'pigmentation', label: 'Pigmentation / Dark Spots', icon: '🎨' },
  { id: 'premature_aging', label: 'Premature Aging / Wrinkles', icon: '👴' },
  { id: 'other', label: 'Other Concerns', icon: '❓' },
];

const WellnessPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAppSelector((state) => state.profile);
  
  // Consent state
  const [consent, setConsent] = useState<WellnessConsent>({
    healthDataConsent: false,
    anonymousMode: false,
    expertContactConsent: false,
  });
  const [consentGiven, setConsentGiven] = useState(false);
  
  // Active module state
  const [activeModule, setActiveModule] = useState<HealthModuleType | null>(null);
  
  // Module-specific states
  const [hormonalInput, setHormonalInput] = useState<Partial<HormonalHealthInput>>({
    fatigueLevel: 5,
    sleepQuality: 5,
    stressLevel: 5,
  });
  
  const [intimateInput, setIntimateInput] = useState<Partial<IntimateHealthInput>>({
    gender: (profile?.gender === 'male' || profile?.gender === 'female') ? profile.gender : undefined,
    duration: 1,
    stressLevel: 5,
    sleepQuality: 5,
    isAnonymous: true,
  });
  
  const [skinHairInput, setSkinHairInput] = useState<Partial<SkinHairHealthInput>>({
    concerns: [],
    severity: 'mild',
    stressLevel: 5,
    sleepHours: 7,
    waterIntake: 8,
    dietQuality: 5,
  });
  
  const [mentalInput, setMentalInput] = useState<Partial<MentalHealthInput>>({
    stressLevel: 5,
    anxietyLevel: 5,
    sleepHours: 7,
    sleepQuality: 5,
    moodRating: 5,
    exerciseFrequency: 'occasional',
    socialConnection: 5,
  });

  // Result state
  const [result, setResult] = useState<{
    riskLevel: RiskLevel;
    consultRecommended: boolean;
    message: string;
    recommendations: string[];
  } | null>(null);

  useEffect(() => {
    // Load saved consent from profile
    if (profile?.wellness?.consent) {
      setConsent(profile.wellness.consent);
      setConsentGiven(profile.wellness.consent.healthDataConsent);
    }
  }, [profile]);

  const handleConsentSubmit = () => {
    if (consent.healthDataConsent) {
      setConsentGiven(true);
      // TODO: Save consent to backend
    }
  };

  const handleModuleSelect = (module: HealthModuleType) => {
    setActiveModule(module);
    setResult(null);
  };

  // Simple rule engine for demonstration
  const analyzeInput = () => {
    let riskLevel: RiskLevel = 'LOW';
    let consultRecommended = false;
    let message = '';
    const recommendations: string[] = [];

    switch (activeModule) {
      case 'HORMONAL':
        if ((hormonalInput.fatigueLevel || 5) >= 8 || hormonalInput.weightChange === 'rapid_gain' || hormonalInput.weightChange === 'rapid_loss') {
          riskLevel = 'MODERATE';
          consultRecommended = true;
          message = 'Based on your inputs, we recommend consulting with an endocrinologist or healthcare provider for a proper evaluation.';
          recommendations.push('Consider getting a comprehensive hormone panel test');
          recommendations.push('Track your symptoms for 2 weeks before consultation');
        } else {
          message = 'Your inputs suggest manageable concerns. Focus on lifestyle improvements.';
          recommendations.push('Prioritize 7-8 hours of quality sleep');
          recommendations.push('Include strength training 3x per week');
          recommendations.push('Manage stress through meditation or yoga');
          recommendations.push('Ensure adequate protein and healthy fats in diet');
        }
        break;

      case 'INTIMATE':
        if (intimateInput.painPresent || intimateInput.bleedingPresent) {
          riskLevel = 'HIGH';
          consultRecommended = true;
          message = '⚠️ The symptoms you described require professional medical attention. Please consult a healthcare provider soon.';
          recommendations.push('Schedule an appointment with a specialist');
          recommendations.push('Note down all symptoms to share with doctor');
        } else if ((intimateInput.duration || 0) >= 3) {
          riskLevel = 'MODERATE';
          consultRecommended = true;
          message = 'Concerns lasting more than 3 months benefit from professional guidance.';
          recommendations.push('Consider consulting a sexual health expert');
          recommendations.push('Focus on stress management');
          recommendations.push('Improve sleep quality');
        } else {
          message = 'Lifestyle factors often play a significant role. Here are some suggestions:';
          recommendations.push('Address stress and sleep quality first');
          recommendations.push('Regular exercise improves overall wellness');
          recommendations.push('Open communication with your partner helps');
          recommendations.push('Give it time - many concerns resolve naturally');
        }
        break;

      case 'SKIN':
        if (skinHairInput.onsetType === 'sudden' || skinHairInput.severity === 'severe') {
          riskLevel = 'MODERATE';
          consultRecommended = true;
          message = 'Sudden onset or severe symptoms warrant professional evaluation.';
          recommendations.push('Consult a dermatologist for proper diagnosis');
          recommendations.push('Avoid self-treatment until professionally evaluated');
        } else {
          message = 'Your skin/hair concerns can often be improved with lifestyle changes:';
          recommendations.push('Drink at least 8 glasses of water daily');
          recommendations.push('Get 7-8 hours of sleep for skin repair');
          recommendations.push('Reduce processed foods and sugar');
          recommendations.push('Manage stress - it directly affects skin health');
          recommendations.push('Consider a consistent skincare routine');
        }
        break;

      case 'MENTAL':
        if ((mentalInput.stressLevel || 5) >= 8 || (mentalInput.anxietyLevel || 5) >= 8) {
          riskLevel = 'MODERATE';
          consultRecommended = true;
          message = 'High stress/anxiety levels benefit from professional support.';
          recommendations.push('Consider speaking with a mental health counselor');
          recommendations.push('Practice grounding exercises daily');
          recommendations.push('Limit caffeine and improve sleep hygiene');
        } else if ((mentalInput.stressLevel || 5) >= 6) {
          message = 'Moderate stress can be managed with self-care:';
          recommendations.push('Try 5-minute daily meditation');
          recommendations.push('Take short walks in nature');
          recommendations.push('Practice deep breathing exercises');
          recommendations.push('Maintain social connections');
        } else {
          message = 'Your mental wellness seems stable. Keep up the good work!';
          recommendations.push('Continue your current self-care routine');
          recommendations.push('Stay physically active');
          recommendations.push('Maintain good sleep habits');
          recommendations.push('Check in with yourself regularly');
        }
        break;
    }

    setResult({ riskLevel, consultRecommended, message, recommendations });
  };

  // Consent screen
  if (!consentGiven) {
    return (
      <div className="wellness-page">
        <div className="wellness-container consent-screen">
          <div className="consent-header">
            <h1>🌿 Wellness Center</h1>
            <p className="subtitle">Your journey to holistic health starts here</p>
          </div>

          <div className="consent-intro">
            <h2>Before We Begin</h2>
            <p>
              The Wellness Center covers sensitive health topics including hormonal health, 
              intimate wellness, skin/hair concerns, and mental well-being. Your privacy 
              and comfort are our top priorities.
            </p>
          </div>

          <div className="consent-options">
            <label className="consent-option">
              <input
                type="checkbox"
                checked={consent.healthDataConsent}
                onChange={(e) => setConsent(prev => ({ ...prev, healthDataConsent: e.target.checked }))}
              />
              <div className="option-content">
                <span className="option-title">I consent to health data processing</span>
                <span className="option-desc">
                  Allow the platform to process my wellness inputs to provide personalized education and insights. 
                  This data is never shared with third parties without explicit consent.
                </span>
              </div>
            </label>

            <label className="consent-option">
              <input
                type="checkbox"
                checked={consent.anonymousMode}
                onChange={(e) => setConsent(prev => ({ ...prev, anonymousMode: e.target.checked }))}
              />
              <div className="option-content">
                <span className="option-title">Enable Anonymous Mode (Recommended)</span>
                <span className="option-desc">
                  For sensitive topics like intimate health, your queries and inputs remain fully anonymous 
                  and are not linked to your profile.
                </span>
              </div>
            </label>

            <label className="consent-option">
              <input
                type="checkbox"
                checked={consent.expertContactConsent}
                onChange={(e) => setConsent(prev => ({ ...prev, expertContactConsent: e.target.checked }))}
              />
              <div className="option-content">
                <span className="option-title">Allow Expert Contact</span>
                <span className="option-desc">
                  When recommended, allow verified experts to be suggested for professional consultation. 
                  You control when and if to contact them.
                </span>
              </div>
            </label>
          </div>

          <div className="consent-disclaimer">
            <p>
              <strong>⚠️ Important:</strong> The Wellness Center provides educational information only. 
              It does not diagnose, prescribe, or replace professional medical advice. 
              Always consult qualified healthcare providers for medical concerns.
            </p>
          </div>

          <button 
            className="consent-submit-btn"
            onClick={handleConsentSubmit}
            disabled={!consent.healthDataConsent}
          >
            Continue to Wellness Center
          </button>

          <button 
            className="back-btn"
            onClick={() => navigate(-1)}
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  // Main wellness dashboard
  return (
    <div className="wellness-page">
      <div className="wellness-container">
        <div className="wellness-header">
          <h1>🌿 Wellness Center</h1>
          <p className="subtitle">Explore holistic health with education and guidance</p>
          {consent.anonymousMode && (
            <div className="anonymous-badge">🔒 Anonymous Mode Active</div>
          )}
        </div>

        {/* Module Selection */}
        {!activeModule && (
          <div className="module-selection">
            <h2>Choose a Health Area</h2>
            <div className="modules-grid">
              <div 
                className="module-card hormonal"
                onClick={() => handleModuleSelect('HORMONAL')}
              >
                <div className="module-icon">⚡</div>
                <h3>Hormonal Health</h3>
                <p>Understand hormones and their impact on your body</p>
                <ul className="module-topics">
                  <li>Testosterone & Estrogen</li>
                  <li>Thyroid Function</li>
                  <li>Cortisol & Stress</li>
                  <li>Metabolic Health</li>
                </ul>
              </div>

              <div 
                className="module-card intimate"
                onClick={() => handleModuleSelect('INTIMATE')}
              >
                <div className="module-icon">💝</div>
                <h3>Intimate & Reproductive</h3>
                <p>Private guidance for sensitive wellness concerns</p>
                <ul className="module-topics">
                  <li>Sexual Wellness</li>
                  <li>Reproductive Health</li>
                  <li>Lifestyle Factors</li>
                  <li>Anonymous Support</li>
                </ul>
              </div>

              <div 
                className="module-card skin"
                onClick={() => handleModuleSelect('SKIN')}
              >
                <div className="module-icon">✨</div>
                <h3>Skin & Hair Health</h3>
                <p>Beauty as health - inside out approach</p>
                <ul className="module-topics">
                  <li>Acne & Breakouts</li>
                  <li>Hair Fall Solutions</li>
                  <li>Pigmentation</li>
                  <li>Anti-Aging</li>
                </ul>
              </div>

              <div 
                className="module-card mental"
                onClick={() => handleModuleSelect('MENTAL')}
              >
                <div className="module-icon">🧠</div>
                <h3>Mental & Lifestyle</h3>
                <p>Balance your mind for complete wellness</p>
                <ul className="module-topics">
                  <li>Stress Management</li>
                  <li>Sleep Quality</li>
                  <li>Guided Breathing</li>
                  <li>Emotional Support</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Hormonal Health Module */}
        {activeModule === 'HORMONAL' && !result && (
          <div className="module-content">
            <button className="back-to-modules" onClick={() => setActiveModule(null)}>
              ← Back to Modules
            </button>
            
            <h2>⚡ Hormonal Health Assessment</h2>
            <p className="module-intro">
              Understand how your hormones might be affecting your well-being. 
              This is for educational purposes only.
            </p>

            {/* Hormone Education Cards */}
            <div className="hormone-cards">
              <h3>Understanding Key Hormones</h3>
              <div className="cards-scroll">
                {hormoneCards.map((card) => (
                  <div key={card.hormone} className="hormone-card">
                    <div className="card-header">
                      <span className="card-icon">{card.icon}</span>
                      <h4>{card.title}</h4>
                    </div>
                    <p className="card-desc">{card.description}</p>
                    <div className="card-symptoms">
                      <strong>Signs of Imbalance:</strong>
                      <ul>
                        {card.symptoms.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Input Form */}
            <div className="assessment-form">
              <h3>Quick Self-Assessment</h3>
              
              <div className="form-group">
                <label>How would you rate your energy/fatigue level?</label>
                <div className="slider-container">
                  <span>Low Energy</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={hormonalInput.fatigueLevel || 5}
                    onChange={(e) => setHormonalInput(prev => ({ ...prev, fatigueLevel: parseInt(e.target.value) }))}
                  />
                  <span>High Energy</span>
                </div>
                <div className="slider-value">{hormonalInput.fatigueLevel}/10</div>
              </div>

              <div className="form-group">
                <label>Recent weight changes?</label>
                <select
                  value={hormonalInput.weightChange || ''}
                  onChange={(e) => setHormonalInput(prev => ({ ...prev, weightChange: e.target.value as any }))}
                >
                  <option value="">Select...</option>
                  <option value="stable">Stable</option>
                  <option value="gradual">Gradual change</option>
                  <option value="rapid_gain">Rapid gain (unexplained)</option>
                  <option value="rapid_loss">Rapid loss (unexplained)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Sleep quality (1-10)</label>
                <div className="slider-container">
                  <span>Poor</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={hormonalInput.sleepQuality || 5}
                    onChange={(e) => setHormonalInput(prev => ({ ...prev, sleepQuality: parseInt(e.target.value) }))}
                  />
                  <span>Excellent</span>
                </div>
                <div className="slider-value">{hormonalInput.sleepQuality}/10</div>
              </div>

              <div className="form-group">
                <label>Stress level (1-10)</label>
                <div className="slider-container">
                  <span>Low</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={hormonalInput.stressLevel || 5}
                    onChange={(e) => setHormonalInput(prev => ({ ...prev, stressLevel: parseInt(e.target.value) }))}
                  />
                  <span>High</span>
                </div>
                <div className="slider-value">{hormonalInput.stressLevel}/10</div>
              </div>

              <button className="analyze-btn" onClick={analyzeInput}>
                Get Insights
              </button>
            </div>
          </div>
        )}

        {/* Intimate Health Module */}
        {activeModule === 'INTIMATE' && !result && (
          <div className="module-content intimate-module">
            <button className="back-to-modules" onClick={() => setActiveModule(null)}>
              ← Back to Modules
            </button>
            
            <h2>💝 Intimate & Reproductive Wellness</h2>
            <p className="module-intro">
              This is a safe, private space to explore intimate health topics. 
              {consent.anonymousMode && ' Your inputs are not linked to your profile.'}
            </p>

            <div className="privacy-notice">
              <span className="privacy-icon">🔒</span>
              <span>Your responses here are {consent.anonymousMode ? 'completely anonymous' : 'kept private'}</span>
            </div>

            <div className="assessment-form">
              <div className="form-group">
                <label>I identify as:</label>
                <div className="gender-select">
                  <button
                    className={`gender-btn ${intimateInput.gender === 'male' ? 'selected' : ''}`}
                    onClick={() => setIntimateInput(prev => ({ ...prev, gender: 'male' }))}
                  >
                    ♂️ Male
                  </button>
                  <button
                    className={`gender-btn ${intimateInput.gender === 'female' ? 'selected' : ''}`}
                    onClick={() => setIntimateInput(prev => ({ ...prev, gender: 'female' }))}
                  >
                    ♀️ Female
                  </button>
                </div>
              </div>

              {intimateInput.gender && (
                <>
                  <div className="form-group">
                    <label>What area would you like to explore?</label>
                    <select
                      value={intimateInput.concern || ''}
                      onChange={(e) => setIntimateInput(prev => ({ ...prev, concern: e.target.value }))}
                    >
                      <option value="">Select your concern...</option>
                      <optgroup label={intimateInput.gender === 'male' ? 'Male Health' : 'Female Health'}>
                        {intimateConcerns[intimateInput.gender].map((c, i) => (
                          <option key={i} value={c}>{c}</option>
                        ))}
                      </optgroup>
                      <optgroup label="General Wellness">
                        {intimateConcerns.common.map((c, i) => (
                          <option key={i} value={c}>{c}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>How long have you been experiencing this? (months)</label>
                    <select
                      value={intimateInput.duration || 1}
                      onChange={(e) => setIntimateInput(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                    >
                      <option value={1}>Less than 1 month</option>
                      <option value={2}>1-2 months</option>
                      <option value={3}>3-6 months</option>
                      <option value={6}>6-12 months</option>
                      <option value={12}>More than a year</option>
                    </select>
                  </div>

                  <div className="form-group warning-group">
                    <label>Important: Are you experiencing any of these?</label>
                    <div className="checkbox-group">
                      <label className="warning-checkbox">
                        <input
                          type="checkbox"
                          checked={intimateInput.painPresent || false}
                          onChange={(e) => setIntimateInput(prev => ({ ...prev, painPresent: e.target.checked }))}
                        />
                        <span>Physical pain or discomfort</span>
                      </label>
                      <label className="warning-checkbox">
                        <input
                          type="checkbox"
                          checked={intimateInput.bleedingPresent || false}
                          onChange={(e) => setIntimateInput(prev => ({ ...prev, bleedingPresent: e.target.checked }))}
                        />
                        <span>Unusual bleeding</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Current stress level (1-10)</label>
                    <div className="slider-container">
                      <span>Low</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={intimateInput.stressLevel || 5}
                        onChange={(e) => setIntimateInput(prev => ({ ...prev, stressLevel: parseInt(e.target.value) }))}
                      />
                      <span>High</span>
                    </div>
                    <div className="slider-value">{intimateInput.stressLevel}/10</div>
                  </div>

                  <button className="analyze-btn" onClick={analyzeInput}>
                    Get Guidance
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Skin & Hair Module */}
        {activeModule === 'SKIN' && !result && (
          <div className="module-content">
            <button className="back-to-modules" onClick={() => setActiveModule(null)}>
              ← Back to Modules
            </button>
            
            <h2>✨ Skin & Hair Health</h2>
            <p className="module-intro">
              Beauty is health from the inside out. Let's understand what your skin and hair are telling you.
            </p>

            <div className="assessment-form">
              <div className="form-group">
                <label>What concerns you most? (select all that apply)</label>
                <div className="concerns-grid">
                  {skinHairConcerns.map((concern) => (
                    <label 
                      key={concern.id} 
                      className={`concern-item ${(skinHairInput.concerns || []).includes(concern.id as any) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={(skinHairInput.concerns || []).includes(concern.id as any)}
                        onChange={(e) => {
                          const current = skinHairInput.concerns || [];
                          setSkinHairInput(prev => ({
                            ...prev,
                            concerns: e.target.checked
                              ? [...current, concern.id as any]
                              : current.filter(c => c !== concern.id)
                          }));
                        }}
                      />
                      <span className="concern-icon">{concern.icon}</span>
                      <span className="concern-label">{concern.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>How did it start?</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="onset"
                      value="gradual"
                      checked={skinHairInput.onsetType === 'gradual'}
                      onChange={() => setSkinHairInput(prev => ({ ...prev, onsetType: 'gradual' }))}
                    />
                    Gradual (over time)
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="onset"
                      value="sudden"
                      checked={skinHairInput.onsetType === 'sudden'}
                      onChange={() => setSkinHairInput(prev => ({ ...prev, onsetType: 'sudden' }))}
                    />
                    Sudden (recently)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>How severe would you say it is?</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="severity"
                      value="mild"
                      checked={skinHairInput.severity === 'mild'}
                      onChange={() => setSkinHairInput(prev => ({ ...prev, severity: 'mild' }))}
                    />
                    Mild
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="severity"
                      value="moderate"
                      checked={skinHairInput.severity === 'moderate'}
                      onChange={() => setSkinHairInput(prev => ({ ...prev, severity: 'moderate' }))}
                    />
                    Moderate
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="severity"
                      value="severe"
                      checked={skinHairInput.severity === 'severe'}
                      onChange={() => setSkinHairInput(prev => ({ ...prev, severity: 'severe' }))}
                    />
                    Severe
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Daily water intake (glasses)</label>
                <select
                  value={skinHairInput.waterIntake || 8}
                  onChange={(e) => setSkinHairInput(prev => ({ ...prev, waterIntake: parseInt(e.target.value) }))}
                >
                  <option value={4}>Less than 4</option>
                  <option value={6}>4-6 glasses</option>
                  <option value={8}>6-8 glasses</option>
                  <option value={10}>8-10 glasses</option>
                  <option value={12}>More than 10</option>
                </select>
              </div>

              <div className="form-group">
                <label>Stress level (1-10)</label>
                <div className="slider-container">
                  <span>Low</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={skinHairInput.stressLevel || 5}
                    onChange={(e) => setSkinHairInput(prev => ({ ...prev, stressLevel: parseInt(e.target.value) }))}
                  />
                  <span>High</span>
                </div>
                <div className="slider-value">{skinHairInput.stressLevel}/10</div>
              </div>

              <button 
                className="analyze-btn" 
                onClick={analyzeInput}
                disabled={(skinHairInput.concerns || []).length === 0}
              >
                Get Recommendations
              </button>
            </div>
          </div>
        )}

        {/* Mental Health Module */}
        {activeModule === 'MENTAL' && !result && (
          <div className="module-content">
            <button className="back-to-modules" onClick={() => setActiveModule(null)}>
              ← Back to Modules
            </button>
            
            <h2>🧠 Mental & Lifestyle Balance</h2>
            <p className="module-intro">
              Mental wellness is the foundation of overall health. Let's check in with how you're feeling.
            </p>

            <div className="assessment-form">
              <div className="form-group">
                <label>Current stress level (1-10)</label>
                <div className="slider-container">
                  <span>Calm</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={mentalInput.stressLevel || 5}
                    onChange={(e) => setMentalInput(prev => ({ ...prev, stressLevel: parseInt(e.target.value) }))}
                  />
                  <span>Very Stressed</span>
                </div>
                <div className="slider-value">{mentalInput.stressLevel}/10</div>
              </div>

              <div className="form-group">
                <label>Anxiety level (1-10)</label>
                <div className="slider-container">
                  <span>None</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={mentalInput.anxietyLevel || 5}
                    onChange={(e) => setMentalInput(prev => ({ ...prev, anxietyLevel: parseInt(e.target.value) }))}
                  />
                  <span>Severe</span>
                </div>
                <div className="slider-value">{mentalInput.anxietyLevel}/10</div>
              </div>

              <div className="form-group">
                <label>Hours of sleep per night</label>
                <select
                  value={mentalInput.sleepHours || 7}
                  onChange={(e) => setMentalInput(prev => ({ ...prev, sleepHours: parseInt(e.target.value) }))}
                >
                  <option value={4}>Less than 5 hours</option>
                  <option value={5}>5-6 hours</option>
                  <option value={7}>6-7 hours</option>
                  <option value={8}>7-8 hours</option>
                  <option value={9}>More than 8 hours</option>
                </select>
              </div>

              <div className="form-group">
                <label>Mood rating (1-10)</label>
                <div className="slider-container">
                  <span>😢</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={mentalInput.moodRating || 5}
                    onChange={(e) => setMentalInput(prev => ({ ...prev, moodRating: parseInt(e.target.value) }))}
                  />
                  <span>😊</span>
                </div>
                <div className="slider-value">{mentalInput.moodRating}/10</div>
              </div>

              <div className="form-group">
                <label>Exercise frequency</label>
                <select
                  value={mentalInput.exerciseFrequency || 'occasional'}
                  onChange={(e) => setMentalInput(prev => ({ ...prev, exerciseFrequency: e.target.value as any }))}
                >
                  <option value="none">None</option>
                  <option value="occasional">Occasional (1-2x/week)</option>
                  <option value="regular">Regular (3-4x/week)</option>
                  <option value="daily">Daily</option>
                </select>
              </div>

              <div className="form-group">
                <label>Social connection (1-10)</label>
                <div className="slider-container">
                  <span>Isolated</span>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={mentalInput.socialConnection || 5}
                    onChange={(e) => setMentalInput(prev => ({ ...prev, socialConnection: parseInt(e.target.value) }))}
                  />
                  <span>Well Connected</span>
                </div>
                <div className="slider-value">{mentalInput.socialConnection}/10</div>
              </div>

              <button className="analyze-btn" onClick={analyzeInput}>
                Get Wellness Check
              </button>

              {/* Quick Breathing Exercise */}
              <div className="breathing-exercise">
                <h3>🌬️ Quick Breathing Exercise</h3>
                <p>Take a moment to center yourself with this simple exercise:</p>
                <div className="breathing-steps">
                  <div className="breath-step">
                    <span className="step-num">1</span>
                    <span>Inhale slowly for 4 seconds</span>
                  </div>
                  <div className="breath-step">
                    <span className="step-num">2</span>
                    <span>Hold for 4 seconds</span>
                  </div>
                  <div className="breath-step">
                    <span className="step-num">3</span>
                    <span>Exhale slowly for 6 seconds</span>
                  </div>
                  <div className="breath-step">
                    <span className="step-num">4</span>
                    <span>Repeat 3-5 times</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Display */}
        {result && (
          <div className="results-section">
            <button className="back-to-modules" onClick={() => { setResult(null); setActiveModule(null); }}>
              ← Back to Modules
            </button>

            <div className={`result-card risk-${result.riskLevel.toLowerCase()}`}>
              <div className="result-header">
                <span className="risk-badge">
                  {result.riskLevel === 'LOW' && '✅ Low Risk'}
                  {result.riskLevel === 'MODERATE' && '⚠️ Moderate Attention Needed'}
                  {result.riskLevel === 'HIGH' && '🚨 Consult Recommended'}
                </span>
              </div>

              <div className="result-message">
                <p>{result.message}</p>
              </div>

              <div className="recommendations">
                <h3>📋 Recommendations</h3>
                <ul>
                  {result.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>

              {result.consultRecommended && consent.expertContactConsent && (
                <div className="expert-cta">
                  <h4>👨‍⚕️ Connect with an Expert</h4>
                  <p>Based on your inputs, speaking with a qualified professional would be beneficial.</p>
                  <button className="find-expert-btn" onClick={() => navigate('/chat')}>
                    Find Verified Experts
                  </button>
                </div>
              )}

              <div className="disclaimer">
                <p>
                  <strong>Disclaimer:</strong> This assessment is for educational purposes only and 
                  does not constitute medical advice, diagnosis, or treatment. Always consult with 
                  qualified healthcare professionals for medical concerns.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WellnessPage;
